import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { ICreateDivision } from "./interfaces/create-division.interface";
import { IUpdateDivision } from "./interfaces/update-division.interface";
import DivisionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/division/division.entity";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "division";

@Injectable({
  providedIn: "root",
})
export class DivisionService extends AbstractService<
  DivisionAttributes,
  ICreateDivision,
  IUpdateDivision
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene divisiones con paginación y filtros.
   *
   * Nota: Solo busca en 1 campo (name), por lo que no requiere OR lógico.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: name
   * Array filters: statusId, companyId
   */
  findAllWithCount(
    options: IPagination<DivisionAttributes>,
    dataToFilter: IFilters,
  ): Observable<IPaginationResult<DivisionAttributes>> {

    const hasSearch = !!options.search;

    const query = buildQuery<DivisionAttributes>(
      // Condiciones base
      where('_deleted', Ops.eq(false)),

      // Filtros condicionales
      dataToFilter?.companyId
        ? andWhere('company.id' as keyof DivisionAttributes, Ops.eq(dataToFilter.companyId))
        : noop(),
      dataToFilter?.status && dataToFilter.status.length > 0
        ? andWhere('statusId', Ops.in(...dataToFilter.status))
        : noop(),

      // Búsqueda (solo si hay término)
      hasSearch
        ? andWhere("name", Ops.like(options.search!, "i"))
        : noop(),

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<DivisionAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<DivisionAttributes[]>> {
    const query = buildQuery<DivisionAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["name","externalKeys" ,"id"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DivisionAttributes[]>>(API_URL);
  }
}
